import { useQuery } from '@tanstack/react-query'
import { getMaps, getWeapons } from '@/lib/api'
import { PageMotion } from '@/components/Motion'
import {
  EmptyState,
  ErrorBlock,
  LoadingBlock,
  PageHeader,
  Panel,
} from '@/components/ui'
import { formatNum } from '@/lib/utils'

export default function Weapons() {
  const weapons = useQuery({ queryKey: ['weapons'], queryFn: () => getWeapons(80) })
  const maps = useQuery({ queryKey: ['maps'], queryFn: getMaps })

  return (
    <PageMotion className="space-y-8">
      <PageHeader title="Weapons & maps" subtitle="Global kill and map aggregates" />

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Weapons" flush className="lg:col-span-2">
          {weapons.isLoading ? (
            <LoadingBlock />
          ) : weapons.error ? (
            <div className="p-4">
              <ErrorBlock message="Failed to load weapons" />
            </div>
          ) : (weapons.data ?? []).length === 0 ? (
            <div className="p-4">
              <EmptyState message="No weapon data" />
            </div>
          ) : (
            <div className="table-wrap border-0 rounded-none">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Weapon</th>
                    <th>Kills</th>
                    <th>Avg dist</th>
                    <th>Max dist</th>
                  </tr>
                </thead>
                <tbody>
                  {(weapons.data ?? []).map((w, i) => (
                    <tr key={w.weapon}>
                      <td className="font-mono text-xs text-ink-faint">{i + 1}</td>
                      <td className="font-medium text-ink">{w.weapon || 'Unknown'}</td>
                      <td className="font-mono text-accent-text">{formatNum(w.count)}</td>
                      <td className="font-mono text-xs">
                        {w.avgDistance != null ? `${formatNum(w.avgDistance, 0)}m` : '—'}
                      </td>
                      <td className="font-mono text-xs">
                        {w.maxDistance != null ? `${formatNum(w.maxDistance, 0)}m` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="Maps" flush>
          {maps.isLoading ? (
            <LoadingBlock />
          ) : maps.error ? (
            <div className="p-4">
              <ErrorBlock message="Failed to load maps" />
            </div>
          ) : (maps.data ?? []).length === 0 ? (
            <div className="p-4">
              <EmptyState message="No maps" />
            </div>
          ) : (
            <div className="table-wrap border-0 rounded-none">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Map</th>
                    <th>Missions</th>
                    <th>Kills</th>
                  </tr>
                </thead>
                <tbody>
                  {(maps.data ?? []).map((m) => (
                    <tr key={m.worldname}>
                      <td className="font-medium text-ink">{m.worldname || 'Unknown'}</td>
                      <td className="font-mono">{formatNum(m.missions ?? m.count ?? 0)}</td>
                      <td className="font-mono text-accent-text">
                        {formatNum(m.totalKills ?? 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
    </PageMotion>
  )
}
